import { NavBar } from 'components/NavBar/NavBar'
import { NoItems } from 'components/NoItems-fallback/NoItems'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { MetricCard } from 'components/ui/MetricCard/MetricCard'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Button } from 'components/ui/Button/Button'
import { AlertListRow } from 'components/ui/AlertListRow/AlertListRow'
import { AlertList } from 'components/ui/AlertListRow/AlertListRow.styled'
import { ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { DemoBadge } from 'pages/Operations/Shared/OpsSharedComponents'
import { USE_OPS_MOCKS } from 'pages/Operations/Shared/opsControl'
import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from 'store'
import { useNavigate } from 'react-router-dom'
import { Flex, MainHeadline, PageWrapper } from 'styling/GlobalStyles'
import { BodyText, TitleText } from 'styling/Typography'
import { tokens } from 'styling/tokens'
import useQueryPagination from 'hooks/useQueryPagination'
import { getParams } from 'utils/helpers'
import {
  clearInventoryDashboard,
  getInventoryItemsDashboard,
} from 'features/inventory/inventoryItemsSlice'
import { IInvOpsItem, IInvReorderRowVM } from 'types/inventoryOps.interface'
import {
  formatInvGhs,
  formatInvGhsShort,
  INV_CRITICAL_CAPTION,
  INV_DEMO_ITEMS,
  INV_DEMO_REQUESTS,
  INV_DEMO_TRANSFERS,
  INV_KPIS,
  INV_LOCATION_SPLIT_LABEL,
  INV_LOCATION_VALUES,
  INV_REORDER_CAPTION,
  INV_SITE_STOCK_VALUE_GHS,
  invBelowMin,
  invCriticalItems,
  invLastMovementLabel,
  invLocationItemCount,
  invReorderRows,
  invRequestTone,
  invShortLocation,
  invSlowMovers,
  invStockSplitLabel,
  invStockValueGhs,
  invTotalOnHand,
  invWorstStockoutLabel,
} from '../inventoryDemoData'
import {
  BarFill,
  BarTrack,
  CriticalNums,
  CriticalRow,
  DaysText,
  HeaderLink,
  InvGrid,
  InvKpiStrip,
  InvPageSubtitle,
  InvWidget,
  ItemCellMeta,
  ItemCellName,
  ItemCellText,
  KpiWarnText,
  LocationMeta,
  LocationName,
  LocationRow,
  LocationTopLine,
  LocationValue,
  ReorderTableTight,
  WidgetBody,
  WidgetCaption,
  WidgetLoader,
} from './InventoryDashboard.styled'
import { RaiseProcurementRequestModal } from './RaiseProcurementRequestModal'

const daysColor = (days: number | null): string => {
  if (days == null) return tokens.text.muted
  if (days < 15) return tokens.status.danger.fg
  if (days < 30) return tokens.status.warning.fg
  return tokens.text.muted
}

const daysLabel = (days: number | null): string => (days == null ? '—' : `${days} d`)

export const InventoryDashboard = () => {
  const { dashboard, isLoading } = useSelector((store: RootState) => store.inventoryItems)
  const [params] = useState<any>(getParams({ location }))
  const [reorderModalRow, setReorderModalRow] = useState<IInvReorderRowVM | null>(null)
  const navigate = useNavigate()

  useQueryPagination({
    fetchFunctions: [getInventoryItemsDashboard],
    cleanUpFunctions: [clearInventoryDashboard],
    params,
  })

  const reorderRows: IInvReorderRowVM[] = useMemo(() => {
    if (dashboard?.data?.length) {
      return dashboard.data
        .map((d) => ({
          id: d.id,
          sku: d.sku,
          name: d.name,
          critical: false,
          onHand: d.quantity,
          minLevel: d.minLevel,
          daysToStockout: null,
          leadTimeDays: null,
          suggestedQty: null,
          belowMin: Boolean(d.isLowStock) || d.quantity < d.minLevel,
        }))
        .filter((r) => r.belowMin)
        .sort((a, b) => a.onHand / (a.minLevel || 1) - b.onHand / (b.minLevel || 1))
    }
    return USE_OPS_MOCKS ? invReorderRows(INV_DEMO_ITEMS) : []
  }, [dashboard])

  const realDataHealthy = Boolean(dashboard?.data?.length) && reorderRows.length === 0

  const usingDemoReorder = !dashboard?.data?.length && USE_OPS_MOCKS

  const lowStockCount = dashboard?.lowStockCount || INV_KPIS.belowMinCount

  const criticalItems = USE_OPS_MOCKS ? invCriticalItems(INV_DEMO_ITEMS) : []
  const slowMovers = USE_OPS_MOCKS ? invSlowMovers(INV_DEMO_ITEMS) : []
  const requests = USE_OPS_MOCKS ? INV_DEMO_REQUESTS : []
  const transfers = USE_OPS_MOCKS ? INV_DEMO_TRANSFERS : []
  const locations = USE_OPS_MOCKS ? INV_LOCATION_VALUES : []

  const transitSublabel = INV_DEMO_TRANSFERS.map(
    (t) => `${invShortLocation(t.from)} → ${invShortLocation(t.to)}`,
  ).join(' · ')

  const reorderColumns: IHonestTableColumn<IInvReorderRowVM>[] = [
    {
      id: 'item',
      header: 'ITEM',
      fill: true,
      cell: (row) => (
        <Flex center gap='0.5rem'>
          <ItemCellText>
            <ItemCellName>{row.name}</ItemCellName>
            <ItemCellMeta>{row.sku}</ItemCellMeta>
          </ItemCellText>
          {row.critical && <StatusPill status='accent'>Critical</StatusPill>}
        </Flex>
      ),
    },
    {
      id: 'onHand',
      header: 'ON / MIN',
      cell: (row) => (
        <DaysText $color={row.belowMin ? tokens.status.danger.fg : tokens.text.primary}>
          {row.onHand} / {row.minLevel}
        </DaysText>
      ),
    },
    {
      id: 'days',
      header: 'STOCKOUT',
      cell: (row) => (
        <DaysText $color={daysColor(row.daysToStockout)}>{daysLabel(row.daysToStockout)}</DaysText>
      ),
    },
    {
      id: 'lead',
      header: 'LEAD',
      cell: (row) => (row.leadTimeDays == null ? '—' : `${row.leadTimeDays} d`),
    },
    {
      id: 'suggested',
      header: 'SUGG. QTY',
      cell: (row) =>
        row.suggestedQty == null || row.suggestedQty === 0 ? '—' : `+${row.suggestedQty}`,
    },
    {
      id: 'action',
      header: '',
      align: 'right',
      cell: (row) => (
        <Button variant='secondary' size='sm' onClick={() => setReorderModalRow(row)}>
          Raise request
        </Button>
      ),
    },
  ]

  const slowColumns: IHonestTableColumn<IInvOpsItem>[] = [
    {
      id: 'item',
      header: 'ITEM',
      fill: true,
      cell: (item) => (
        <ItemCellText>
          <ItemCellName>{item.name}</ItemCellName>
          <ItemCellMeta>{item.sku}</ItemCellMeta>
        </ItemCellText>
      ),
    },
    {
      id: 'onHand',
      header: 'ON HAND',
      cell: (item) => `${invTotalOnHand(item)}`,
    },
    {
      id: 'value',
      header: 'STOCK VALUE',
      cell: (item) => formatInvGhs(invStockValueGhs(item)),
    },
    {
      id: 'lastMovement',
      header: 'LAST MOVEMENT',
      cell: (item) => invLastMovementLabel(item.lastMovementDaysAgo),
    },
    {
      id: 'flag',
      header: 'FLAG',
      cell: (item) =>
        item.movement === 'OBSOLETE' ? (
          <StatusPill status='neutral'>OBSOLETE?</StatusPill>
        ) : (
          <StatusPill status='warning'>SLOW</StatusPill>
        ),
    },
  ]

  return (
    <>
      <NavBar />
      <PageWrapper noFooter>
        <Flex center gap='0.75rem'>
          <MainHeadline>Inventory</MainHeadline>
          <DemoBadge />
        </Flex>
        <InvPageSubtitle>
          Stock health, reorder risk and movement across site stores
        </InvPageSubtitle>
        {USE_OPS_MOCKS && (
          <InvKpiStrip>
            <MetricCard
              value={formatInvGhsShort(INV_SITE_STOCK_VALUE_GHS)}
              label='STOCK VALUE'
              sublabel={INV_LOCATION_SPLIT_LABEL}
              accentColor={tokens.accent.base}
            />
            <MetricCard
              value={INV_KPIS.skuCount}
              label='SKUS'
              sublabel={<KpiWarnText>{INV_KPIS.belowMinCount} below minimum</KpiWarnText>}
              accentColor={tokens.accent.base}
            />
            <MetricCard
              value={lowStockCount}
              label='LOW STOCK'
              sublabel={invWorstStockoutLabel()}
              accentColor={tokens.status.danger.fg}
            />
            <MetricCard
              value={INV_KPIS.openRequests}
              label='OPEN REQUESTS'
              sublabel={<KpiWarnText>{INV_KPIS.urgentRequests} urgent</KpiWarnText>}
              accentColor={tokens.status.warning.fg}
            />
            <MetricCard
              value={formatInvGhsShort(INV_KPIS.receiptsWeekGhs)}
              label='RECEIPTS THIS WEEK'
              sublabel={`${INV_KPIS.receiptsWeekCount} receipts posted`}
              accentColor={tokens.status.success.fg}
            />
            <MetricCard
              value={INV_KPIS.inTransitCount}
              label='IN TRANSIT'
              sublabel={transitSublabel}
              accentColor={tokens.status.info.fg}
            />
          </InvKpiStrip>
        )}
        <InvGrid>
          <InvWidget $span={6}>
            <ContainerHeader>
              <Flex width='100%' between center>
                <TitleText>Reorder now</TitleText>
                <HeaderLink type='button' onClick={() => navigate('/inventory/stock')}>
                  All items ›
                </HeaderLink>
              </Flex>
            </ContainerHeader>
            <ContainerContent>
              {isLoading ? (
                <Flex width='100%' height='100%' justcenter center>
                  <WidgetLoader />
                </Flex>
              ) : reorderRows.length ? (
                <>
                  <ReorderTableTight>
                    <HonestTable
                      columns={reorderColumns}
                      data={reorderRows.slice(0, 6)}
                      keyField='id'
                    />
                  </ReorderTableTight>
                  {usingDemoReorder && <WidgetCaption>{INV_REORDER_CAPTION}</WidgetCaption>}
                </>
              ) : realDataHealthy ? (
                <BodyText grey>Nothing needs reordering — every item is above its minimum.</BodyText>
              ) : (
                <NoItems title='Reorder candidates' />
              )}
            </ContainerContent>
          </InvWidget>

          <InvWidget $span={6}>
            <ContainerHeader>
              <Flex width='100%' between center>
                <Flex center gap='0.5rem'>
                  <TitleText>Critical spares</TitleText>
                  <DemoBadge />
                </Flex>
                <HeaderLink type='button' onClick={() => navigate('/inventory/stock')}>
                  View items ›
                </HeaderLink>
              </Flex>
            </ContainerHeader>
            <WidgetBody>
              {criticalItems.length ? (
                <>
                  {criticalItems.map((item) => {
                    const below = invBelowMin(item)
                    return (
                      <CriticalRow key={item.id}>
                        <ItemCellText>
                          <ItemCellName>{item.name}</ItemCellName>
                          <ItemCellMeta>
                            {item.sku} · {invStockSplitLabel(item)}
                          </ItemCellMeta>
                        </ItemCellText>
                        <CriticalNums>
                          {invTotalOnHand(item)} / min {item.minLevel}
                        </CriticalNums>
                        {below ? (
                          <StatusPill status='danger' dot>
                            BELOW MIN
                          </StatusPill>
                        ) : (
                          <StatusPill status='success'>Ok</StatusPill>
                        )}
                      </CriticalRow>
                    )
                  })}
                  <WidgetCaption>{INV_CRITICAL_CAPTION}</WidgetCaption>
                </>
              ) : (
                <NoItems title='Critical spares' />
              )}
            </WidgetBody>
          </InvWidget>

          <InvWidget $span={4}>
            <ContainerHeader>
              <Flex width='100%' between center>
                <Flex center gap='0.5rem'>
                  <TitleText>Open requests</TitleText>
                  <DemoBadge />
                </Flex>
                <HeaderLink type='button' onClick={() => navigate('/inventory/requests')}>
                  View all ›
                </HeaderLink>
              </Flex>
            </ContainerHeader>
            <WidgetBody>
              {requests.length ? (
                <AlertList>
                  {requests.map((req) => (
                    <AlertListRow
                      key={req.id}
                      title={`${req.id} · ${req.items}`}
                      meta={`${req.department} · ${req.ageLabel}`}
                      pillLabel={req.statusLabel}
                      pillTone={invRequestTone(req.status)}
                      onClick={() => navigate('/inventory/requests')}
                    />
                  ))}
                </AlertList>
              ) : (
                <NoItems title='Open requests' />
              )}
            </WidgetBody>
          </InvWidget>

          <InvWidget $span={4}>
            <ContainerHeader>
              <Flex width='100%' between center>
                <Flex center gap='0.5rem'>
                  <TitleText>Stock by location</TitleText>
                  <DemoBadge />
                </Flex>
              </Flex>
            </ContainerHeader>
            <WidgetBody>
              {locations.length ? (
                locations.map((loc) => (
                  <LocationRow key={loc.location}>
                    <LocationTopLine>
                      <LocationName>{loc.location}</LocationName>
                      <LocationMeta>
                        {invLocationItemCount(INV_DEMO_ITEMS, loc.location)} items
                      </LocationMeta>
                      <LocationValue>{formatInvGhsShort(loc.valueGhs)}</LocationValue>
                    </LocationTopLine>
                    <BarTrack>
                      <BarFill $pct={Math.round((loc.valueGhs / INV_SITE_STOCK_VALUE_GHS) * 100)} />
                    </BarTrack>
                  </LocationRow>
                ))
              ) : (
                <NoItems title='Stock locations' />
              )}
            </WidgetBody>
          </InvWidget>

          <InvWidget $span={4}>
            <ContainerHeader>
              <Flex width='100%' between center>
                <Flex center gap='0.5rem'>
                  <TitleText>In transit</TitleText>
                  <DemoBadge />
                </Flex>
                <HeaderLink type='button' onClick={() => navigate('/inventory/transfers')}>
                  View all ›
                </HeaderLink>
              </Flex>
            </ContainerHeader>
            <WidgetBody>
              {transfers.length ? (
                <AlertList>
                  {transfers.map((t) => (
                    <AlertListRow
                      key={t.id}
                      title={`${t.id} · ${t.items}`}
                      meta={`${invShortLocation(t.from)} → ${invShortLocation(t.to)} · dispatched ${t.dispatchedLabel}`}
                      pillLabel={t.statusLabel}
                      pillTone='info'
                      onClick={() => navigate('/inventory/transfers')}
                    />
                  ))}
                </AlertList>
              ) : (
                <NoItems title='Transfers in transit' />
              )}
            </WidgetBody>
          </InvWidget>

          <InvWidget $span={12}>
            <ContainerHeader>
              <Flex width='100%' between center>
                <Flex center gap='0.5rem'>
                  <TitleText>Slow movers</TitleText>
                  <DemoBadge />
                </Flex>
                <HeaderLink type='button' onClick={() => navigate('/inventory/stock')}>
                  View all ›
                </HeaderLink>
              </Flex>
            </ContainerHeader>
            <ContainerContent>
              {slowMovers.length ? (
                <>
                  <HonestTable columns={slowColumns} data={slowMovers} keyField='id' />
                  <WidgetCaption>
                    No movement in 6+ months — review for write-down or disposal.
                  </WidgetCaption>
                </>
              ) : (
                <NoItems title='Slow movers' />
              )}
            </ContainerContent>
          </InvWidget>
        </InvGrid>
        {reorderModalRow && (
          <RaiseProcurementRequestModal
            context={{
              inventoryItemId: usingDemoReorder ? null : Number(reorderModalRow.id),
              name: reorderModalRow.name,
              sku: reorderModalRow.sku,
              suggestedQty: reorderModalRow.suggestedQty,
              onHand: reorderModalRow.onHand,
              minLevel: reorderModalRow.minLevel,
            }}
            closeModal={() => setReorderModalRow(null)}
          />
        )}
      </PageWrapper>
    </>
  )
}
